import Icons from 'components/atoms/Icons';
import React, { useEffect, useState } from 'react';
import getOsfViewportSize from 'assets/utils/getOsfViewportSize';

type SearchPaginationControlsProps = {
  count: number;
  pageSize: number;
  page: number;
  updatePage: (page: number) => void;
};

const SearchPaginationControls = ({
  count,
  pageSize,
  page,
  updatePage,
}: SearchPaginationControlsProps) => {
  const [size, setSize] = useState(getOsfViewportSize());
  const numberOfPages = Math.ceil(count / pageSize);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      const viewportSize = getOsfViewportSize();
      setSize(viewportSize);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  if (numberOfPages <= 1) {
    return null;
  }

  const renderPageButton = (pageNum: number, className: string) => (
    <li
      className={
        className +
        ' page-item' +
        (pageNum === page
          ? ' active'
          : pageNum === page + 1
            ? ' next'
            : pageNum === page - 1
              ? ' prev'
              : '')
      }
      key={pageNum}
    >
      <button
        className="page-link"
        onClick={() => updatePage(pageNum)}
        disabled={pageNum === page}
        aria-label={`${pageNum}`}
        aria-current={pageNum === page ? 'page' : undefined}
      >
        {pageNum}
      </button>
    </li>
  );

  const renderEllipsis = (key: string, className: string) => (
    <li className={className + ' page-item'} key={key}>
      <div className="page-link ellipsis">
        <span>...</span>
      </div>
    </li>
  );

  const renderMobilePageNumbers = () => {
    const pages = [];

    if (numberOfPages <= 4) {
      for (let i = 1; i <= numberOfPages; i++) {
        pages.push(renderPageButton(i, 'pagination-mobile'));
      }
    } else {
      pages.push(renderPageButton(1, 'pagination-mobile'));

      if (page <= 2) {
        pages.push(renderPageButton(2, 'pagination-mobile'));
        pages.push(renderEllipsis('end-ellipsis', 'pagination-mobile'));
        pages.push(renderPageButton(numberOfPages, 'pagination-mobile'));
      } else if (page > numberOfPages - 2) {
        pages.push(renderEllipsis('start-ellipsis', 'pagination-mobile'));
        pages.push(renderPageButton(numberOfPages - 1, 'pagination-mobile'));
        pages.push(renderPageButton(numberOfPages, 'pagination-mobile'));
      } else {
        pages.push(renderEllipsis('start-ellipsis', 'pagination-mobile'));
        pages.push(renderPageButton(page, 'pagination-mobile'));
        pages.push(renderEllipsis('end-ellipsis', 'pagination-mobile'));
        pages.push(renderPageButton(numberOfPages, 'pagination-mobile'));
      }
    }
    return pages;
  };

  const renderDesktopPageNumbers = () => {
    const pages = [];

    if (numberOfPages <= 5) {
      for (let i = 1; i <= numberOfPages; i++) {
        pages.push(renderPageButton(i, 'pagination-desktop'));
      }
    } else {
      if (page === 1) {
        pages.push(renderPageButton(1, 'pagination-desktop'));
        pages.push(renderPageButton(2, 'pagination-desktop'));
        pages.push(renderEllipsis('end-ellipsis', 'pagination-desktop'));
        pages.push(renderPageButton(numberOfPages, 'pagination-desktop'));
      } else if (page <= 3) {
        if (page - 1 !== 1) {
          pages.push(renderPageButton(1, 'pagination-desktop'));
        }
        pages.push(renderPageButton(page - 1, 'pagination-desktop'));
        pages.push(renderPageButton(page, 'pagination-desktop'));
        pages.push(renderPageButton(page + 1, 'pagination-desktop'));
        pages.push(renderEllipsis('end-ellipsis', 'pagination-desktop'));
        pages.push(renderPageButton(numberOfPages, 'pagination-desktop'));
      } else if (page > numberOfPages - 3) {
        pages.push(renderPageButton(1, 'pagination-desktop'));
        pages.push(renderEllipsis('start-ellipsis', 'pagination-desktop'));
        pages.push(renderPageButton(page - 1, 'pagination-desktop'));
        pages.push(renderPageButton(page, 'pagination-desktop'));
        if (!(page + 1 >= numberOfPages)) {
          pages.push(renderPageButton(page + 1, 'pagination-desktop'));
        }
        if (page !== numberOfPages) {
          pages.push(renderPageButton(numberOfPages, 'pagination-desktop'));
        }
      } else {
        pages.push(renderPageButton(1, 'pagination-desktop'));
        pages.push(renderEllipsis('start-ellipsis', 'pagination-desktop'));
        pages.push(renderPageButton(page - 1, 'pagination-desktop'));
        pages.push(renderPageButton(page, 'pagination-desktop'));
        pages.push(renderPageButton(page + 1, 'pagination-desktop'));
        pages.push(renderEllipsis('end-ellipsis', 'pagination-desktop'));
        pages.push(renderPageButton(numberOfPages, 'pagination-desktop'));
      }
    }

    return pages;
  };

  return (
    <nav className="search-pagination-nav" aria-label="Page Navigation">
      <ul className="pagination">
        <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
          <button
            aria-label="Previous Page"
            className="page-link"
            onClick={() => updatePage(page - 1)}
            disabled={page === 1}
          >
            <span aria-hidden="true">
              <Icons id="pld-chevron-left" className="chevron-left" />
            </span>
          </button>
        </li>

        {size == 'xs' || size === 'sm'
          ? renderMobilePageNumbers()
          : renderDesktopPageNumbers()}

        <li className={`page-item ${page === numberOfPages ? 'disabled' : ''}`}>
          <button
            aria-label="Next Page"
            className="page-link"
            onClick={() => updatePage(page + 1)}
            disabled={page === numberOfPages}
          >
            <span aria-hidden="true">
              <Icons id="pld-chevron-right" className="chevron-right" />
            </span>
          </button>
        </li>
      </ul>
    </nav>
  );
};

export default SearchPaginationControls;
